import { AfterViewInit, ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { select, Store } from '@ngrx/store';

import { each, has, isNil, isNull } from 'lodash-es';
import { combineLatest, map, Observable, tap, timer } from 'rxjs';
import { filter } from 'rxjs/operators';

import { MainTabBasePathEnum } from '../../../../../../enums/main-tabs';
import {
  PrimarySubscriptionAccessActionEnum,
  PrimarySubscriptionPlanAccessAliasEnum
} from '../../../../../../enums/monetization';
import { ProfileSearchEnum } from '../../../../../../enums/profile-search';
import { TooltipEnum } from '../../../../../../enums/tooltip';
import { WalkingFarmNewTabEnum } from '../../../../../../enums/walking-farm';
import * as fromMapActions from '../../../../../../store/actions/map.actions';
import * as fromWalkingFarmActions from '../../../../../../store/actions/walking-farm.actions';
import * as fromMapSelectors from '../../../../../../store/selectors/map.selectors';
import * as fromWalkingFarmSelectors from '../../../../../../store/selectors/walking-farm.selectors';
import { MonetizationService } from '../../../../../services/monetization.service';
import { TooltipService } from '../../../../../services/tooltip.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-new',
  standalone: false,
  styleUrls: ['./new-farm.component.scss'],
  templateUrl: './new-farm.component.html'
})
export class NewFarmComponent implements AfterViewInit {
  private activatedRoute = inject(ActivatedRoute);
  private monetizationService = inject(MonetizationService);
  private store = inject(Store);
  private tooltipService = inject(TooltipService);

  mapInstance: google.maps.Map | null = null;
  selectedMapTypeId: google.maps.MapTypeId | null = null;

  mapCenter$: Observable<google.maps.LatLngLiteral>;
  mapOptions$: Observable<google.maps.MapOptions>;

  markerOptions$: Observable<google.maps.MarkerOptions>;
  searchLocation$: Observable<google.maps.LatLngLiteral | null>;

  drawFarmPoints$: Observable<google.maps.LatLngLiteral[]>;
  drawFarmCompleted$: Observable<boolean>;

  drawMode$: Observable<boolean>;
  drawModeType$: Observable<WalkingFarmNewTabEnum>;
  drawFarmRadius$: Observable<number>;
  drawFarmRadiusInMeters$: Observable<number>;
  drawFarmCenter$: Observable<google.maps.LatLngLiteral | null>;

  drawFarmRadiusBounds?: google.maps.LatLngBounds | null;
  drawFarmPolygonBounds?: google.maps.LatLngBounds | null;

  drawFarmMarkerOptions$: Observable<google.maps.MarkerOptions>;

  isDrawModeEnabled = false;
  drawModeType: WalkingFarmNewTabEnum = WalkingFarmNewTabEnum.Polygon;

  centerOnPins = false;

  polyLineOptions: google.maps.PolylineOptions = {
    geodesic: true,
    strokeColor: '#3E6787',
    strokeOpacity: 1,
    strokeWeight: 2
  };

  polyLinePointsOptions: google.maps.CircleOptions = {
    fillColor: '#3E6787',
    fillOpacity: 0.8,
    radius: 4,
    strokeColor: '#3E6787',
    strokeOpacity: 0.8,
    strokePosition: 2,
    strokeWeight: 8
  };

  polygonOptions: google.maps.PolygonOptions = {
    fillColor: '#0879D1',
    fillOpacity: 0.4,
    strokeColor: '#3E6787',
    strokeOpacity: 1,
    strokeWeight: 2
  };

  circleOptions: google.maps.CircleOptions = {
    fillColor: '#3E6787',
    fillOpacity: 0.4,
    strokeColor: '#3E6787',
    strokeOpacity: 1,
    strokeWeight: 2
  };

  defaultBackHref: string | undefined;

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];

    this.mapOptions$ = this.store.pipe(select(fromMapSelectors.selectOptions));
    this.mapCenter$ = this.store.pipe(select(fromMapSelectors.selectCenter));
    this.store.pipe(select(fromMapSelectors.selectCurrentType)).subscribe((selectedMapTypeId) => {
      this.selectedMapTypeId = selectedMapTypeId;
      this.updateMapType();
    });
    this.searchLocation$ = this.store.pipe(select(fromWalkingFarmSelectors.selectAddressSearchLocation));
    this.markerOptions$ = this.store.pipe(
      select(fromMapSelectors.selectMarkerOptions),
      map((markerOptions) => ({
        ...markerOptions,
        icon: {
          scaledSize: new google.maps.Size(26, 44),
          url: '/assets/img/pin-home.png'
        }
      }))
    );
    this.drawFarmMarkerOptions$ = this.store.pipe(
      select(fromMapSelectors.selectMarkerOptions),
      map((markerOptions) => ({
        ...markerOptions,
        icon: {
          scaledSize: new google.maps.Size(26, 44),
          url: '/assets/img/pin-target.png'
        }
      }))
    );

    this.drawMode$ = this.store.pipe(select(fromWalkingFarmSelectors.selectDrawMode)).pipe(
      tap(async (isDrawModeEnabled) => {
        this.isDrawModeEnabled = isDrawModeEnabled;
        await this.showDrawModeNotification();
      })
    );
    this.drawModeType$ = this.store.pipe(select(fromWalkingFarmSelectors.selectDrawModeType)).pipe(
      tap((drawModeType) => {
        this.drawModeType = drawModeType;
      })
    );
    this.drawFarmPoints$ = this.store.pipe(select(fromWalkingFarmSelectors.selectDrawFarmPoints)).pipe(
      tap((points) => {
        if (points.length > 0 && this.drawModeType === WalkingFarmNewTabEnum.Polygon) {
          this.drawFarmPolygonBounds = new google.maps.LatLngBounds();
          each(points, (point) => {
            this.drawFarmPolygonBounds?.extend(point);
          });
          this.updateMapBounds();
        } else {
          this.drawFarmPolygonBounds = null;
        }
      })
    );
    this.drawFarmCompleted$ = this.store.pipe(select(fromWalkingFarmSelectors.selectDrawFarmCompleted));
    this.drawFarmRadius$ = this.store.pipe(select(fromWalkingFarmSelectors.selectDrawFarmRadius));
    this.drawFarmRadiusInMeters$ = this.store.pipe(select(fromWalkingFarmSelectors.selectDrawFarmRadiusInMeters));
    this.drawFarmCenter$ = this.store.pipe(select(fromWalkingFarmSelectors.selectDrawFarmCenter));

    combineLatest([
      this.store.pipe(select(fromWalkingFarmSelectors.selectDrawFarmCenter)),
      this.store.pipe(select(fromWalkingFarmSelectors.selectDrawFarmRadiusInMeters))
    ]).subscribe(([center, meters]) => {
      if (center) {
        const circle = new google.maps.Circle({
          center,
          radius: meters
        });
        this.drawFarmRadiusBounds = circle.getBounds();
        this.updateMapBounds();
      } else {
        this.drawFarmRadiusBounds = null;
      }
    });

    this.activatedRoute.queryParams.pipe(filter((params) => has(params, 'centerOnPins'))).subscribe(async (params) => {
      this.centerOnPins = !!params['centerOnPins'];
      if (this.centerOnPins) {
        setTimeout(() => {
          this.updateMapBounds();
        }, 300);
      }
    });
  }

  ngAfterViewInit() {
    this.updateMapType();
    this.updateMapBounds();
  }

  mapInitialized(mapInstance: google.maps.Map) {
    this.mapInstance = mapInstance;
    this.updateMapType();
    this.updateMapBounds();
  }

  updateMapType() {
    if (!isNull(this.selectedMapTypeId)) {
      this.mapInstance?.setMapTypeId(this.selectedMapTypeId);
    }
  }

  updateMapBounds() {
    if (this.mapInstance) {
      switch (this.drawModeType) {
        case WalkingFarmNewTabEnum.Polygon:
          if (this.centerOnPins && !isNil(this.drawFarmPolygonBounds) && !this.drawFarmPolygonBounds.isEmpty()) {
            this.centerOnPins = false;
            this.mapInstance?.fitBounds(this.drawFarmPolygonBounds);
          }
          break;
        case WalkingFarmNewTabEnum.Radius:
          if (!isNil(this.drawFarmRadiusBounds) && !this.drawFarmRadiusBounds.isEmpty()) {
            if (this.centerOnPins) {
              this.centerOnPins = false;
            }
            this.mapInstance?.fitBounds(this.drawFarmRadiusBounds);
          }
          break;
      }
    }
  }

  mapDragend() {
    if (this.mapInstance) {
      const center = this.mapInstance.getCenter();
      if (center) {
        this.store.dispatch(
          fromMapActions.updateCenter({
            payload: { location: center.toJSON() }
          })
        );
      }
    }
  }

  async toggleDrawMode() {
    await this.tooltipService.dismissAll();
    const hasAccess = await this.monetizationService.accessFeature(
      [
        PrimarySubscriptionPlanAccessAliasEnum.A,
        PrimarySubscriptionPlanAccessAliasEnum.B,
        PrimarySubscriptionPlanAccessAliasEnum.C
      ],
      { accessAction: PrimarySubscriptionAccessActionEnum.CreateFarm }
    );
    if (hasAccess) {
      this.store.dispatch(fromWalkingFarmActions.toggleDrawMode());
    }
  }

  onMapClick(event: google.maps.MapMouseEvent) {
    if (event.latLng && this.isDrawModeEnabled) {
      if (this.drawModeType === WalkingFarmNewTabEnum.Radius) {
        this.store.dispatch(
          fromWalkingFarmActions.setDrawFarmCenter({
            payload: {
              center: event.latLng.toJSON()
            }
          })
        );
      } else {
        this.store.dispatch(fromWalkingFarmActions.addDrawFarmPoint({ payload: { point: event.latLng.toJSON() } }));
      }
    }
  }

  onPolyLinePointClick(point: google.maps.LatLngLiteral) {
    if (this.isDrawModeEnabled && this.drawModeType === WalkingFarmNewTabEnum.Polygon) {
      this.store.dispatch(fromWalkingFarmActions.addDrawFarmFinalPoint({ payload: { point } }));
    }
  }

  undoDrawFarmPoint() {
    if (this.isDrawModeEnabled && this.drawModeType === WalkingFarmNewTabEnum.Polygon) {
      this.store.dispatch(fromWalkingFarmActions.undoDrawFarmPoint());
    }
  }

  clearDrawFarm() {
    if (this.isDrawModeEnabled) {
      this.store.dispatch(fromWalkingFarmActions.clearDrawFarm());
      this.drawFarmPolygonBounds = null;
      this.drawFarmRadiusBounds = null;
    }
  }

  autoFillDrawFarm() {
    if (this.isDrawModeEnabled && this.drawModeType === WalkingFarmNewTabEnum.Polygon) {
      this.store.dispatch(fromWalkingFarmActions.autoFillDrawFarm());
    }
  }

  loadFarmSummary() {
    if (this.isDrawModeEnabled) {
      if (this.drawModeType === WalkingFarmNewTabEnum.Radius) {
        this.store.dispatch(
          fromWalkingFarmActions.loadRadiusFarmSummary({
            payload: {
              searchType:
                this.defaultBackHref === MainTabBasePathEnum.PropertySearch
                  ? ProfileSearchEnum.PropertySearchWalkingFarm
                  : ProfileSearchEnum.WalkingFarm
            }
          })
        );
      } else {
        this.store.dispatch(
          fromWalkingFarmActions.loadPolygonFarmSummary({
            payload: {
              searchType:
                this.defaultBackHref === MainTabBasePathEnum.PropertySearch
                  ? ProfileSearchEnum.PropertySearchWalkingFarm
                  : ProfileSearchEnum.WalkingFarm
            }
          })
        );
      }
    }
  }

  async drawModeTypeChange(event: CustomEvent) {
    this.clearDrawFarm();
    this.store.dispatch(
      fromWalkingFarmActions.setDrawModeType({
        payload: {
          type: event.detail.value
        }
      })
    );

    await this.showDrawModeNotification(event.detail.value);
  }

  async showDrawModeNotification(type: WalkingFarmNewTabEnum = WalkingFarmNewTabEnum.Polygon) {
    if (this.isDrawModeEnabled) {
      timer(300).subscribe(async () => {
        await this.tooltipService.show(
          {
            buttons: [
              {
                htmlAttributes: {
                  'aria-label': 'close'
                },
                icon: 'close'
              }
            ],
            color: 'light',
            message:
              this.drawModeType === WalkingFarmNewTabEnum.Polygon
                ? 'Tap to place points around your polygon farm. Complete the shape by tapping the first point.'
                : 'Tap on the map to place the center point of your radius farm.',
            position: 'top',
            positionAnchor: 'mode-tabs'
          },
          type === WalkingFarmNewTabEnum.Polygon ? TooltipEnum.NewFarmPolygon : TooltipEnum.NewFarmRadius
        );
      });
    }
  }

  drawFarmRadiusChange(event: CustomEvent) {
    this.store.dispatch(
      fromWalkingFarmActions.setDrawFarmRadius({
        payload: {
          radius: event.detail.value
        }
      })
    );
  }

  protected readonly walkingFarmNewTabs = WalkingFarmNewTabEnum;
  protected readonly minRadiusRangeValue = 0.1;
}
